<script lang="ts">
  import PauseIcon from '$lib/assets/icons/PauseIcon.svelte';
  import PlayIcon from '$lib/assets/icons/PlayIcon.svelte';
  import RetryIcon from '$lib/assets/icons/RetryIcon.svelte';
  import { onMount, type ComponentType } from 'svelte';

  export let currentTime: number;
  export let duration: number;
  export let isPlaying: boolean;
  export let onSeek: (time: number) => void;
  export let onPlayPause: () => void;
  export let videoElement: HTMLVideoElement | null;

  export let PauseIconComponent: ComponentType = PauseIcon;
  export let PlayIconComponent: ComponentType = PlayIcon;
  export let RetryIconComponent: ComponentType = RetryIcon;

  $: progressPercentage = 0;
  $: remainingTime = duration ? formatTime(duration) : '00:00';

  onMount(() => {
    if (videoElement) {
      getButtonIcon();
    }
  });
  // Calculate the percentage of video progress
  function formatTime(time: number): string {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  function handleSeek() {
    const timeToSeek = (progressPercentage / 100) * duration;
    onSeek(timeToSeek);
  }

  $: if (currentTime > 0) {
    progressPercentage = (currentTime / duration) * 100;
    remainingTime = formatTime(duration - currentTime);
  }

  const getButtonIcon = () => {
    if (!videoElement?.paused) {
      return PauseIconComponent;
    } else if (!isPlaying && currentTime === duration && currentTime !== 0) {
      return RetryIconComponent;
    } else {
      return PlayIconComponent;
    }
  };
</script>

<div class="video-progress-bar">
  <!-- Play/Pause button -->
  <button class="play-pause-button" on:click={onPlayPause}>
    <svelte:component this={getButtonIcon()} />
  </button>

  <!-- Seeker bar with dynamically calculated background color -->
  <input
    type="range"
    min="0"
    max="100"
    step="0.5"
    bind:value={progressPercentage}
    on:input={handleSeek}
    style="background: linear-gradient(to right, white 0%, white {progressPercentage}%, grey {progressPercentage}%, grey 100%);"
  />
  <p class="remaining-time-text">{remainingTime}</p>
</div>

<style>
  .video-progress-bar {
    display: flex;
    align-items: center;
    transition: width 5s linear;
  }

  .remaining-time-text {
    color: var(--color-text-light);
  }

  .play-pause-button {
    background-color: transparent;
    border: none;
    padding: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--color-text-light);
    color: grey;
    height: 5px;
    appearance: none;
    cursor: pointer;
    margin: 0 1rem;
    transition: all 0.5s ease-out;
  }

  input[type='range']:not(:hover)::-webkit-slider-thumb {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin-top: -12px;
    transition: all 0.2s ease;
  }

  input[type='range']:not(:hover)::-moz-range-track {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -12px;
  }
</style>
